import { useEffect, useRef } from "react";
import { SupportTicketMessage } from "src/api/rtk/indorsoftRu/slices/supportApiSlice";
import { smartInitialScroll } from "../utils/supportTicketScroll";

type UseSupportTicketScrollProps = {
    messages: SupportTicketMessage[];
    lastReadSupportId: number;
    ticketId: string | undefined;
    messagesListRef: React.RefObject<HTMLUListElement>;
};

export function useSupportTicketScroll({
    messages,
    lastReadSupportId,
    ticketId,
    messagesListRef,
}: UseSupportTicketScrollProps) {
    // Ведём историю, для какого LAST_READ_SUPPORT_MESSAGE_ID уже делали initial scroll
    const initialScrollLastReadIdRef = useRef<number | null>(null);
    // Отслеживаем ticketId, для которого уже выполнен initial scroll
    // Это предотвращает скролл при polling-обновлениях
    const initialScrollTicketIdRef = useRef<string | null>(null);
    // Флаг, что initial scroll уже выполнен для текущего ticketId
    const hasPerformedInitialScrollRef = useRef<boolean>(false);

    // начальный скролл:
    // - скроллим до последнего прочитанного сообщения / последнего сообщения пользователя
    // - только при первоначальной загрузке тикета или изменении ticketId
    // - не выполняем при polling-обновлениях
    useEffect(() => {
        if (!messages.length || !ticketId) return;
        const list = messagesListRef.current;
        if (!list) return;

        // Если ticketId изменился, сбрасываем историю скролла
        if (initialScrollTicketIdRef.current !== ticketId) {
            initialScrollTicketIdRef.current = ticketId;
            initialScrollLastReadIdRef.current = null;
            hasPerformedInitialScrollRef.current = false;
        }

        // Если уже делали initial scroll для этого ticketId — не выполняем снова
        // Это предотвращает скролл при polling-обновлениях
        if (hasPerformedInitialScrollRef.current) {
            return;
        }

        // Если уже делали initial scroll для этого конкретного lastReadSupportId — выходим
        if (initialScrollLastReadIdRef.current === lastReadSupportId) {
            return;
        }

        // ждём кадр, чтобы DOM точно обновился
        requestAnimationFrame(() => {
            smartInitialScroll(list, messages, lastReadSupportId);
            initialScrollLastReadIdRef.current = lastReadSupportId;
            hasPerformedInitialScrollRef.current = true;
        });
    }, [messages, lastReadSupportId, ticketId, messagesListRef]);
}


